import { useEffect, useState } from "react";
import { Bell, TrendingUp, Users, Tag, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminTokenRevenue from "@/components/admin/AdminTokenRevenue";

export default function AdminAlerts() {
  const [stats, setStats] = useState(null);
  const [digesting, setDigesting] = useState(false);

  useEffect(() => {
    api.get("/admin/saved-searches/stats").then((r) => setStats(r.data)).catch(() => setStats({ total: 0, by_category: [], by_keyword: [] }));
  }, []);

  const runDigest = async () => {
    setDigesting(true);
    try {
      const r = await api.post("/admin/run-supplier-digest");
      const d = r.data;
      toast.success(`Digest trimis: ${d.suppliers_notified} furnizori · ${d.total_matches} cereri (${d.requests_scanned} scanate în 24h).`);
    } catch {
      toast.error("Nu am putut rula digestul. Încearcă din nou.");
    } finally {
      setDigesting(false);
    }
  };

  if (!stats) return <div className="border border-slate-200 p-8 text-center text-slate-400">Se încarcă...</div>;

  const maxCat = Math.max(1, ...(stats.by_category || []).map((c) => c.count));
  const maxKw = Math.max(1, ...(stats.by_keyword || []).map((k) => k.count));

  return (
    <div data-testid="admin-alerts">
      <AdminTokenRevenue />
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-bold text-lg mb-1">Cereri & Alerte — hartă a cererii</h2>
          <p className="text-sm text-slate-500">Unde este cea mai mare cerere: pentru achiziții țintite.</p>
        </div>
        <button onClick={runDigest} disabled={digesting}
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors disabled:opacity-50"
          data-testid="run-supplier-digest">
          {digesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Trimite digest furnizori (24h)
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="border border-slate-200 p-5 flex items-center gap-3" data-testid="stat-total-alerts">
          <Bell className="w-6 h-6 text-orange-600" />
          <div><div className="text-2xl font-black">{stats.total}</div><div className="text-xs uppercase tracking-wide text-slate-500">Alerte active</div></div>
        </div>
        <div className="border border-slate-200 p-5 flex items-center gap-3" data-testid="stat-unique-emails">
          <Users className="w-6 h-6 text-slate-700" />
          <div><div className="text-2xl font-black">{stats.unique_emails}</div><div className="text-xs uppercase tracking-wide text-slate-500">Abonați unici</div></div>
        </div>
        <div className="border border-slate-200 p-5 flex items-center gap-3" data-testid="stat-with-keyword">
          <Tag className="w-6 h-6 text-slate-700" />
          <div><div className="text-2xl font-black">{stats.with_keyword || 0}</div><div className="text-xs uppercase tracking-wide text-slate-500">Cu cuvânt cheie</div></div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 font-bold text-sm uppercase tracking-wide mb-3"><TrendingUp className="w-4 h-4 text-orange-600" /> Categorii cerute</div>
          {(stats.by_category || []).length === 0 && <div className="text-slate-400 text-sm">Nicio alertă pe categorii încă.</div>}
          <div className="space-y-2">
            {(stats.by_category || []).map((c) => (
              <div key={c.category} data-testid={`cat-demand-${c.category}`}>
                <div className="flex justify-between text-sm mb-0.5"><span className="text-slate-700 truncate pr-2">{c.name}</span><span className="font-bold text-orange-600">{c.count}</span></div>
                <div className="h-2 bg-slate-100"><div className="h-2 bg-orange-500" style={{ width: `${(c.count / maxCat) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 font-bold text-sm uppercase tracking-wide mb-3"><Tag className="w-4 h-4 text-orange-600" /> Cuvinte cheie populare</div>
          {(stats.by_keyword || []).length === 0 && <div className="text-slate-400 text-sm">Niciun cuvânt cheie încă.</div>}
          <div className="flex flex-wrap gap-2">
            {(stats.by_keyword || []).map((k) => (
              <span key={k.keyword} data-testid={`kw-demand-${k.keyword}`}
                className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-sm"
                style={{ background: `rgba(234,88,12,${0.08 + 0.5 * (k.count / maxKw)})` }}>
                {k.keyword} <span className="font-bold text-orange-700">{k.count}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
